import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Typography, Radio, Spin, Progress, Input, message, Space, Tag, Form } from 'antd';
import { SearchOutlined, BulbOutlined, EditOutlined, CheckOutlined, ReloadOutlined, GlobalOutlined, ScanOutlined } from '@ant-design/icons';
import '../../styles/mobile.css';
import SEOHead from '../../components/SEOHead';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const AutoBlog = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [generatedContent, setGeneratedContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [scanningMessage, setScanningMessage] = useState('');

  // Mock trending topics data
  const mockTopics = [
    {
      id: 1,
      trend: "AI Safety for Children",
      title: "How to Teach Your Child About AI Safety: A Parent's Guide to the Digital Future",
      subheader: "As artificial intelligence becomes part of daily life, here's how to prepare your children for safe AI interactions",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=250&fit=crop",
      popularity: "Trending +340%",
      category: "Digital Parenting"
    },
    {
      id: 2,
      trend: "Emotional Regulation Techniques",
      title: "5 Science-Backed Emotional Regulation Techniques That Actually Work for Sensitive Children",
      subheader: "Research-proven methods to help highly sensitive children manage overwhelming emotions and build resilience",
      image: "https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=400&h=250&fit=crop",
      popularity: "Trending +280%",
      category: "Child Development"
    },
    {
      id: 3,
      trend: "Screen-Free Learning Activities",
      title: "50 Screen-Free Activities That Boost Your Toddler's Brain Development",
      subheader: "Creative, engaging alternatives to screen time that support cognitive growth and family bonding",
      image: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=400&h=250&fit=crop",
      popularity: "Trending +195%",
      category: "Early Learning"
    },
    {
      id: 4,
      trend: "Holiday Stress Management",
      title: "Managing Holiday Stress When You Have Anxious Children: Expert Strategies That Work",
      subheader: "Practical tips from child psychologists to help anxious children navigate holiday overwhelm with confidence",
      image: "https://images.unsplash.com/photo-1512389098783-66b81f86e199?w=400&h=250&fit=crop",
      popularity: "Trending +156%",
      category: "Family Wellness"
    },
    {
      id: 5,
      trend: "Mindful Bedtime Routines",
      title: "The 10-Minute Mindful Bedtime Routine That Transforms Sleep for the Whole Family",
      subheader: "Simple mindfulness practices that help children (and parents) wind down and achieve deeper, more restful sleep",
      image: "https://images.unsplash.com/photo-1520637836862-4d197d17c90a?w=400&h=250&fit=crop",
      popularity: "Trending +143%",
      category: "Sleep & Wellness"
    }
  ];

  const steps = [
    { title: 'Enter Website', icon: <GlobalOutlined />, description: 'Enter your website URL for analysis' },
    { title: 'Analyzing Website', icon: <ScanOutlined />, description: 'Scanning your website to understand your business' },
    { title: 'Discovering Trends', icon: <SearchOutlined />, description: 'Finding trending topics relevant to your industry' },
    { title: 'Generating Ideas', icon: <BulbOutlined />, description: 'Creating compelling titles and subheaders with AI' },
    { title: 'Creating Visuals', icon: <EditOutlined />, description: 'Generating custom images with DALL-E' },
    { title: 'Select & Generate', icon: <CheckOutlined />, description: 'Choose your topic and create full content' },
    { title: 'Edit & Publish', icon: <EditOutlined />, description: 'Review and customize your blog post' }
  ];

  useEffect(() => {
    if (currentStep >= 2 && currentStep < 5) {
      const timer = setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  // Simulate website scanning with progressive messages
  useEffect(() => {
    if (currentStep === 1) {
      const messages = [
        'Reading website content...',
        'Identifying products and services...',
        'Analyzing brand voice and tone...',
        'Understanding target audience...',
        'Preparing topic recommendations...'
      ];
      
      let messageIndex = 0;
      setScanningMessage(messages[0]);
      
      const messageInterval = setInterval(() => {
        messageIndex++;
        if (messageIndex < messages.length) {
          setScanningMessage(messages[messageIndex]);
        } else {
          clearInterval(messageInterval);
          setTimeout(() => setCurrentStep(2), 1000);
        }
      }, 800);
      
      return () => clearInterval(messageInterval);
    }
  }, [currentStep]);

  const handleTopicSelect = (topicId) => {
    setSelectedTopic(topicId);
  };

  const analyzeWebsite = () => {
    if (!websiteUrl.trim()) {
      message.warning('Please enter a website URL');
      return;
    }
    
    // Basic URL validation
    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    if (!urlPattern.test(websiteUrl)) {
      message.error('Please enter a valid website URL');
      return;
    }
    
    setCurrentStep(1);
  };

  const generateContent = () => {
    if (!selectedTopic) {
      message.warning('Please select a topic first');
      return;
    }
    
    setIsLoading(true);
    
    // Simulate content generation
    setTimeout(() => {
      const topic = mockTopics.find(t => t.id === selectedTopic);
      const sampleContent = `# ${topic.title}

${topic.subheader}

## Introduction

In today's rapidly evolving world, parents face unique challenges that previous generations never encountered. This comprehensive guide provides evidence-based strategies and practical solutions to help you navigate modern parenting with confidence.

## Key Points to Remember

- **Evidence-Based Approach**: All recommendations are backed by current research in child development and psychology
- **Age-Appropriate Strategies**: Techniques are tailored for different developmental stages
- **Family-Centered Solutions**: Methods that work for the whole family, not just individual children

## Practical Strategies

### 1. Creating a Supportive Environment

The foundation of success lies in creating an environment where children feel safe to express themselves and learn. Here's how to establish that foundation:

- Set clear, consistent boundaries
- Provide predictable routines
- Celebrate small wins and progress
- Practice patience and understanding

### 2. Building Emotional Intelligence

Helping children understand and manage their emotions is crucial for long-term success:

- Validate their feelings before addressing behavior
- Teach emotion vocabulary
- Model healthy emotional expression
- Practice problem-solving together

### 3. Encouraging Independence

While providing support, it's important to foster independence:

- Offer choices within appropriate limits
- Encourage problem-solving attempts
- Gradually increase responsibilities
- Celebrate independent successes

## Expert Insights

Leading child psychologists emphasize the importance of consistency and patience in implementing these strategies. Dr. Sarah Johnson notes: "The key is not perfection, but persistent, gentle guidance that helps children develop their own coping mechanisms."

## Conclusion

Remember that every child is unique, and what works for one may need adjustment for another. The most important thing is to stay connected with your child and adapt these strategies to fit your family's needs.

*Ready to learn more about supporting your child's emotional development? Explore our collection of comfort companions designed to help children build confidence and emotional regulation skills.*`;

      setGeneratedContent(sampleContent);
      setCurrentStep(6);
      setIsLoading(false);
    }, 1500);
  };

  const handleContentChange = (e) => {
    setGeneratedContent(e.target.value);
  };

  const publishContent = () => {
    message.success('Content published successfully! (Demo mode)');
  };

  const resetDemo = () => {
    setCurrentStep(0);
    setSelectedTopic(null);
    setGeneratedContent('');
    setIsLoading(false);
    setWebsiteUrl('');
    setScanningMessage('');
  };

  const progressPercent = ((currentStep + 1) / 7) * 100;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <SEOHead 
        title="AutoBlog - AI-Powered Content Generation | Lumibears"
        description="Experience the future of content creation with AutoBlog. Generate trending, high-quality blog posts automatically using AI-powered research, writing, and image generation."
        keywords="AutoBlog, AI content generation, automated blogging, content creation, AI writing, blog automation"
        canonical="https://lumibears.com/autoblog"
      />

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <Title level={1} style={{ color: '#6B8CAE', marginBottom: '16px' }}>
          AutoBlog MVP Demo
        </Title>
        <Paragraph style={{ fontSize: '18px', color: '#666', maxWidth: '600px', margin: '0 auto' }}>
          Experience the complete AutoBlog workflow: from trending topic discovery to AI-generated content ready for publication.
        </Paragraph>
        
        <Progress 
          percent={progressPercent} 
          status="active" 
          strokeColor="#6B8CAE"
          style={{ margin: '20px 0' }}
        />
        
        <Text strong>{steps[currentStep]?.title}</Text>
        <br />
        <Text type="secondary">{steps[currentStep]?.description}</Text>
      </div>

      {/* Step Content */}
      {currentStep === 0 && (
        <Card style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Title level={3}>Enter Your Website URL</Title>
          <Paragraph style={{ marginBottom: '30px' }}>
            Enter your website URL so we can analyze your business and create relevant content recommendations.
          </Paragraph>
          
          <Form layout="vertical" style={{ maxWidth: '400px', margin: '0 auto' }}>
            <Form.Item
              label="Website URL"
              help="Example: mystore.com, myblog.org, mycompany.net"
            >
              <Input
                size="large"
                placeholder="Enter your website URL"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                prefix={<GlobalOutlined />}
                onPressEnter={analyzeWebsite}
              />
            </Form.Item>
            <Form.Item>
              <Button 
                type="primary" 
                size="large" 
                onClick={analyzeWebsite}
                style={{ backgroundColor: '#6B8CAE', borderColor: '#6B8CAE' }}
                icon={<ScanOutlined />}
              >
                Analyze Website
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      {currentStep === 1 && (
        <Card style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '20px' }}>
            <Title level={3}>Analyzing Your Website</Title>
            <Paragraph style={{ fontSize: '16px', color: '#666' }}>
              {scanningMessage}
            </Paragraph>
            <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <Text strong>Website: </Text>
              <Text code>{websiteUrl}</Text>
            </div>
          </div>
        </Card>
      )}

      {currentStep >= 2 && currentStep < 5 && (
        <Card style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '20px' }}>
            <Title level={3}>{steps[currentStep]?.title}</Title>
            <Paragraph>{steps[currentStep]?.description}</Paragraph>
          </div>
        </Card>
      )}

      {currentStep === 5 && (
        <div>
          <Card style={{ marginBottom: '30px', backgroundColor: '#f8f9fa' }}>
            <Title level={4} style={{ color: '#6B8CAE', marginBottom: '16px' }}>
              Website Analysis Complete ✓
            </Title>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px' }}>
                  <Text strong>Business Type:</Text>
                  <br />
                  <Text>Child Wellness & Parenting</Text>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px' }}>
                  <Text strong>Target Audience:</Text>
                  <br />
                  <Text>Parents of children aged 2-12</Text>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px' }}>
                  <Text strong>Content Focus:</Text>
                  <br />
                  <Text>Emotional wellness, development</Text>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px' }}>
                  <Text strong>Brand Voice:</Text>
                  <br />
                  <Text>Warm, expert, supportive</Text>
                </div>
              </Col>
            </Row>
          </Card>

          <Title level={2} style={{ textAlign: 'center', marginBottom: '30px' }}>
            Select Your Topic
          </Title>
          
          <Paragraph style={{ textAlign: 'center', marginBottom: '30px', color: '#666' }}>
            Based on your website analysis, here are trending topics perfect for your audience:
          </Paragraph>
          
          <Radio.Group 
            value={selectedTopic} 
            onChange={(e) => handleTopicSelect(e.target.value)}
            style={{ width: '100%' }}
          >
            <Row gutter={[16, 16]}>
              {mockTopics.map((topic) => (
                <Col key={topic.id} xs={24} md={12} lg={12}>
                  <Radio value={topic.id} style={{ width: '100%' }}>
                    <Card 
                      hoverable
                      cover={
                        <img 
                          alt={topic.title} 
                          src={topic.image} 
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                      }
                      style={{ 
                        border: selectedTopic === topic.id ? '2px solid #6B8CAE' : '1px solid #f0f0f0',
                        margin: '8px 0'
                      }}
                    >
                      <div style={{ marginBottom: '12px' }}>
                        <Tag color="blue">{topic.category}</Tag>
                        <Tag color="green">{topic.popularity}</Tag>
                      </div>
                      <Title level={4} style={{ marginBottom: '8px', fontSize: '16px' }}>
                        {topic.title}
                      </Title>
                      <Paragraph style={{ color: '#666', fontSize: '14px' }}>
                        {topic.subheader}
                      </Paragraph>
                    </Card>
                  </Radio>
                </Col>
              ))}
            </Row>
          </Radio.Group>

          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <Space>
              <Button onClick={resetDemo} icon={<ReloadOutlined />}>
                Start Over
              </Button>
              <Button 
                type="primary" 
                size="large" 
                onClick={generateContent}
                loading={isLoading}
                disabled={!selectedTopic}
                style={{ backgroundColor: '#6B8CAE', borderColor: '#6B8CAE' }}
              >
                Generate Full Content
              </Button>
            </Space>
          </div>
        </div>
      )}

      {currentStep === 6 && (
        <div>
          <Title level={2} style={{ textAlign: 'center', marginBottom: '30px' }}>
            Edit Your Generated Content
          </Title>
          
          <Card>
            <TextArea
              value={generatedContent}
              onChange={handleContentChange}
              rows={25}
              style={{ fontFamily: 'monospace', fontSize: '14px' }}
              placeholder="Your generated content will appear here..."
            />
            
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <Space>
                <Button onClick={resetDemo} icon={<ReloadOutlined />}>
                  Start Over
                </Button>
                <Button 
                  type="primary" 
                  size="large" 
                  onClick={publishContent}
                  style={{ backgroundColor: '#6B8CAE', borderColor: '#6B8CAE' }}
                >
                  Publish Content
                </Button>
              </Space>
            </div>
          </Card>

          <Card style={{ marginTop: '20px', backgroundColor: '#f8f9fa' }}>
            <Title level={4}>Content Preview</Title>
            <div 
              style={{ 
                whiteSpace: 'pre-line',
                lineHeight: '1.6',
                fontSize: '16px'
              }}
            >
              {generatedContent.split('\n').map((line, index) => {
                if (line.startsWith('# ')) {
                  return <Title key={index} level={1}>{line.substring(2)}</Title>;
                } else if (line.startsWith('## ')) {
                  return <Title key={index} level={2}>{line.substring(3)}</Title>;
                } else if (line.startsWith('### ')) {
                  return <Title key={index} level={3}>{line.substring(4)}</Title>;
                } else if (line.trim()) {
                  return <Paragraph key={index}>{line}</Paragraph>;
                } else {
                  return <br key={index} />;
                }
              })}
            </div>
          </Card>
        </div>
      )}

      {/* Demo Info */}
      <Card style={{ marginTop: '40px', backgroundColor: '#f6f8fa', border: '1px dashed #d1d9e0' }}>
        <Title level={4} style={{ color: '#6B8CAE' }}>About This Demo</Title>
        <Paragraph>
          This is a frontend-only mockup demonstrating the AutoBlog MVP workflow. In the full implementation, this would connect to:
        </Paragraph>
        <ul>
          <li><strong>Web Search API</strong> for real trending topic discovery</li>
          <li><strong>OpenAI ChatGPT</strong> for title and content generation</li>
          <li><strong>DALL-E API</strong> for custom image creation</li>
          <li><strong>Your CMS</strong> for direct publishing integration</li>
        </ul>
        <Paragraph>
          The complete AutoBlog platform would handle brand voice customization, SEO optimization, 
          quality scoring, and webhook-based content delivery to your existing website.
        </Paragraph>
      </Card>
    </div>
  );
};

export default AutoBlog;